import React from 'react'
import { ActionObject, BlankViewImageSize, BlankViewImageSizes, BlankViewImageVariant } from '../types'
import { Box, Button } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Typography } from '@e3dc-react/shell/Elements/Typography'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { getThemedBlankViewImage } from 'src/Libs/utils'

interface StyleProps {
  imageSize: BlankViewImageSize
  fullHeight: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  blankViewWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: theme.spacing(2),
    overflow: 'hidden',
    width: '100%',
    height: props.fullHeight ? '100%' : '80%',
    justifyContent: 'center',
    paddingBottom: theme.spacing(1),
  },
  image: {
    width: props.imageSize === BlankViewImageSizes.Small ? '120px' : '180px',
  },
}))

interface Props {
  imageVariant: BlankViewImageVariant
  title: string
  explanation?: string
  action?: ActionObject | React.ReactElement
  imageSize?: BlankViewImageSize
  fullHeight?: boolean
}

/**
 * This component provides a general blank view.
 * Every blank view contain an image/icon, title and explanation to inform why nothing is displayed.
 * Often a blank view also includes a clear action to move on from the current situation (nothing displayed).
 */
const BlankView: React.FC<Props> = ({ imageVariant, title, explanation, action, fullHeight = false, imageSize = BlankViewImageSizes.Large }) => {
  const { classes } = useStyles({ imageSize, fullHeight })
  const { t } = useCustomTranslation()
  const isActionObject = (action?: React.ReactElement | ActionObject): action is ActionObject => {
    return Boolean(action && typeof action === 'object' && 'text' in action && 'onClick' in action)
  }

  const imageToUse = getThemedBlankViewImage(imageVariant)

  return (
    <Box className={classes.blankViewWrapper} data-qa={'blank-view'}>
      <img src={imageToUse} alt="" className={classes.image} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '760px' }}>
        <Typography variant="h1">{t(title)}</Typography>
        {explanation && (
          <Typography variant="h4" align="center">
            {t(explanation)}
          </Typography>
        )}
      </Box>
      {isActionObject(action) && (
        <Button variant="contained" onClick={action.onClick} data-qa={'blank-view-action-button'}>
          {t(action.text)}
        </Button>
      )}
      {!isActionObject(action) && <Box>{action}</Box>}
    </Box>
  )
}

export default BlankView
